import type { AppContext } from '@application/context/AppContext';
import type { Tenant } from '@shared/types/Tenant';
import { SqliteEntityBridge } from '@infra/persistence/SqliteEntityBridge';
import { logger } from '@infra/telemetry/Logger';

/** Tables locales chiffrées utilisées en mode distant (hors entités métier API). */
export const REMOTE_SECURE_LOCAL_TABLES = [
  'tenants',
  'module_data',
  'remote_cache',
  'sync_outbox',
] as const;

export interface RemoteSecureCacheBootstrapResult {
  activated: boolean;
  skipped?: boolean;
  already?: boolean;
  tenantsMigrated?: number;
  reason?: string;
}

/**
 * Active SQLite SQLCipher comme cache local chiffré en mode REMOTE_MULTI_STATION.
 * Migre uniquement le profil tenant depuis Dexie (pas salaries/factures/etc.).
 */
export class RemoteSecureCacheBootstrap {
  static async ensure(ctx: AppContext): Promise<RemoteSecureCacheBootstrapResult> {
    if (!SqliteEntityBridge.isAvailable()) {
      return { activated: false, skipped: true, reason: 'hors Electron' };
    }

    if (await SqliteEntityBridge.isSqlitePrimary()) {
      return { activated: true, already: true, tenantsMigrated: 0 };
    }

    const result = await SqliteEntityBridge.ensureRemoteSecureCache();
    if (!result.ok) {
      logger.warn({
        event: 'remote_secure_cache_failed',
        error: result.error ?? 'activation impossible',
      });
      return {
        activated: false,
        reason: result.error ?? 'activation SQLite distante impossible',
      };
    }

    const tenantsMigrated = await RemoteSecureCacheBootstrap.migrateTenantsFromDexie(ctx);
    logger.info({
      event: 'remote_secure_cache_activated',
      already: result.already ?? false,
      tenantsMigrated,
      storageProfile: result.storageProfile,
    });

    return {
      activated: true,
      already: result.already,
      tenantsMigrated,
    };
  }

  private static async migrateTenantsFromDexie(ctx: AppContext): Promise<number> {
    const tenants = (await ctx.db.tenants.toArray()) as Tenant[];
    let count = 0;
    for (const tenant of tenants) {
      await SqliteEntityBridge.put('tenants', tenant);
      count += 1;
    }
    return count;
  }
}
